// BEGIN FILE: src/astro/karmic/frictionQuestions.ts
// Planet-friction question bank.
// DESIGN RULES:
//   • No planet name appears in question text (preserved throughout the UX)
//   • Higher score = MORE friction (0=none, 4=very often)
//   • 3 questions per planet = 27 total
//   • Internal question IDs use abbreviated planet codes (sun_, moon_, etc.)

export type PlanetName = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';

export interface FrictionQuestion {
  id: string;       // internal key, never shown to user
  planet: PlanetName;  // internal, never shown to user
  text: string;     // the actual question text
}

// User-facing energy label
export const PLANET_ENERGY_LABELS: Record<PlanetName, string> = {
  Sun:     'Sun Energy',
  Moon:    'Moon Energy',
  Mars:    'Mars Energy',
  Mercury: 'Mercury Energy',
  Jupiter: 'Jupiter Energy',
  Venus:   'Venus Energy',
  Saturn:  'Saturn Energy',
  Rahu:    'Rahu Energy',
  Ketu:    'Ketu Energy',
};

export const PLANET_ENERGY_ICONS: Record<PlanetName, string> = {
  Sun:     '☀️',
  Moon:    '🌙',
  Mars:    '⚔️',
  Mercury: '💬',
  Jupiter: '🌿',
  Venus:   '💎',
  Saturn:  '⚖️',
  Rahu:    '🌀',
  Ketu:    '🔮',
};

export const FRICTION_QUESTIONS: FrictionQuestion[] = [
  // ─── SUN (Identity, confidence, authority) ────────────────────────────────
  { id: 'sun_q1', planet: 'Sun',
    text: 'I feel unsure about my direction or purpose in life.' },
  { id: 'sun_q2', planet: 'Sun',
    text: 'I struggle to take initiative or step into a leadership role.' },
  { id: 'sun_q3', planet: 'Sun',
    text: 'I depend on others\' approval before I feel confident acting.' },
  { id: 'sun_q4', planet: 'Sun',
    text: 'I feel unnoticed or unrecognised even when I put in real effort.' },
  { id: 'sun_q5', planet: 'Sun',
    text: 'I hesitate to take responsibility in important situations.' },

  // ─── MOON (Mind, emotions, stability) ────────────────────────────────────
  { id: 'moon_q1', planet: 'Moon',
    text: 'I feel mentally heavy or low without a clear reason.' },
  { id: 'moon_q2', planet: 'Moon',
    text: 'My mood fluctuates frequently during the day.' },
  { id: 'moon_q3', planet: 'Moon',
    text: 'I feel anxious or emotionally overwhelmed easily.' },
  { id: 'moon_q4', planet: 'Moon',
    text: 'I struggle to feel calm even when things are okay.' },
  { id: 'moon_q5', planet: 'Moon',
    text: 'I feel emotionally dependent on how others respond to me.' },

  // ─── MARS (Energy, anger, action) ─────────────────────────────────────────
  { id: 'mars_q1', planet: 'Mars',
    text: 'I react quickly with anger or frustration.' },
  { id: 'mars_q2', planet: 'Mars',
    text: 'I feel impatient when things slow down or get delayed.' },
  { id: 'mars_q3', planet: 'Mars',
    text: 'I take impulsive actions I later regret.' },
  { id: 'mars_q4', planet: 'Mars',
    text: 'I get into conflicts or arguments more often than I would like.' },
  { id: 'mars_q5', planet: 'Mars',
    text: 'I feel excess energy but struggle to channel it properly.' },

  // ─── MERCURY (Thinking, communication) ────────────────────────────────────
  { id: 'mer_q1', planet: 'Mercury',
    text: 'I find it hard to make clear decisions.' },
  { id: 'mer_q2', planet: 'Mercury',
    text: 'I overthink simple situations.' },
  { id: 'mer_q3', planet: 'Mercury',
    text: 'I struggle to express my thoughts or ideas clearly to others.' },
  { id: 'mer_q4', planet: 'Mercury',
    text: 'I second-guess myself frequently after making a choice.' },
  { id: 'mer_q5', planet: 'Mercury',
    text: 'I get confused or stressed when handling multiple tasks at once.' },

  // ─── JUPITER (Growth, wisdom, guidance) ───────────────────────────────────
  { id: 'jup_q1', planet: 'Jupiter',
    text: 'I feel stuck despite putting in effort to grow or improve.' },
  { id: 'jup_q2', planet: 'Jupiter',
    text: 'I lack clarity about what I am working towards long-term.' },
  { id: 'jup_q3', planet: 'Jupiter',
    text: 'I feel disconnected from any sense of purpose or meaning.' },
  { id: 'jup_q4', planet: 'Jupiter',
    text: 'I struggle to find the right guidance, mentors, or teachers.' },
  { id: 'jup_q5', planet: 'Jupiter',
    text: 'I feel my important decisions lacked wisdom only after the outcome.' },

  // ─── VENUS (Relationships, harmony, comfort) ──────────────────────────────
  { id: 'ven_q1', planet: 'Venus',
    text: 'I experience repeated misunderstandings in close relationships.' },
  { id: 'ven_q2', planet: 'Venus',
    text: 'I feel emotionally disconnected from people I care about.' },
  { id: 'ven_q3', planet: 'Venus',
    text: 'I struggle to maintain harmony in personal or romantic relationships.' },
  { id: 'ven_q4', planet: 'Venus',
    text: 'I seek comfort or pleasure as a way to escape stress.' },
  { id: 'ven_q5', planet: 'Venus',
    text: 'I feel chronically dissatisfied in my love or personal life.' },

  // ─── SATURN (Delay, pressure, karma) ──────────────────────────────────────
  { id: 'sat_q1', planet: 'Saturn',
    text: 'Despite consistent effort, results take far longer than expected.' },
  { id: 'sat_q2', planet: 'Saturn',
    text: 'I feel burdened by responsibilities that weigh on me.' },
  { id: 'sat_q3', planet: 'Saturn',
    text: 'I keep postponing important tasks even when I know I should act.' },
  { id: 'sat_q4', planet: 'Saturn',
    text: 'I feel stuck in the same situation for a long time.' },
  { id: 'sat_q5', planet: 'Saturn',
    text: 'I feel I have to work much harder than others for the same result.' },

  // ─── RAHU (Confusion, obsession, restlessness) ────────────────────────────
  { id: 'rahu_q1', planet: 'Rahu',
    text: 'I feel unsure about important life decisions.' },
  { id: 'rahu_q2', planet: 'Rahu',
    text: 'I get distracted or pulled in multiple directions at once.' },
  { id: 'rahu_q3', planet: 'Rahu',
    text: 'I feel restless without knowing why.' },
  { id: 'rahu_q4', planet: 'Rahu',
    text: 'I chase things that don\'t satisfy me once I have them.' },
  { id: 'rahu_q5', planet: 'Rahu',
    text: 'I feel strongly pulled toward certain habits or thoughts I can\'t shake.' },

  // ─── KETU (Detachment, loss, past karma) ──────────────────────────────────
  { id: 'ketu_q1', planet: 'Ketu',
    text: 'I feel disconnected from things I once cared about deeply.' },
  { id: 'ketu_q2', planet: 'Ketu',
    text: 'I lose interest quickly after achieving something I worked for.' },
  { id: 'ketu_q3', planet: 'Ketu',
    text: 'I feel directionless without being able to explain why.' },
  { id: 'ketu_q4', planet: 'Ketu',
    text: 'I avoid responsibilities or quietly withdraw from situations.' },
  { id: 'ketu_q5', planet: 'Ketu',
    text: 'I feel isolated even when I am surrounded by people.' },
];

export const RESPONSE_LABELS: { value: number; label: string }[] = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Rarely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Very often' },
];

// All 9 planets in survey order
export const PLANET_ORDER: PlanetName[] = [
  'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu',
];

// Questions grouped by planet, in order
export function getQuestionsForPlanet(planet: PlanetName): FrictionQuestion[] {
  return FRICTION_QUESTIONS.filter(q => q.planet === planet);
}
